import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, Receipt, TrendingUp, TrendingDown, Award,
  AlertTriangle, Banknote, Image, Scale, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAuthenticatedClient } from '@/services/client';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { SimpleMetric, SimpleMetricGrid } from '@/components/cbs/SimpleMetric';
import { toast } from 'sonner';
import { useLocaleFormat } from '@/hooks/useLocaleFormat';

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; color: string }> = {
  pending: { label: 'Pending', variant: 'secondary', color: 'text-warning' },
  calculated: { label: 'Calculated', variant: 'outline', color: 'text-info' },
  approved: { label: 'Approved', variant: 'default', color: 'text-primary' },
  paid: { label: 'Paid', variant: 'default', color: 'text-success' },
  disputed: { label: 'Disputed', variant: 'destructive', color: 'text-destructive' },
};

export const SettlementViewScreen: React.FC = () => {
  const { settlementId } = useParams<{ settlementId: string }>();
  const navigate = useNavigate();
  const client = getAuthenticatedClient();
  const queryClient = useQueryClient();
  const { isSimpleMode } = useSimpleMode();
  const { t } = useTranslation();
  const { formatCurrency, formatNumber } = useLocaleFormat();
  // Currency is sourced from the farmer's country (countries.currency_code +
  // currency_symbol via profiles.country_id), carried on the auth user and
  // applied by formatCurrency — no language-based guessing.
  const fmt = (v: number | null | undefined): string =>
    v != null ? formatCurrency(v) : '—';

  const fmtKg = (v: number | null | undefined): string =>
    v != null
      ? `${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
      : '—';

  const [activeTab, setActiveTab] = useState('score');
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  const { data: settlement, isLoading } = useQuery({
    queryKey: ['cbs', 'settlement', settlementId],
    queryFn: async () => {
      const { data, error } = await client
        .from('settlements')
        .select('*, batches(batch_code, breed_id, breeds(name)), organizations(name)')
        .eq('id', settlementId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!settlementId,
  });

  const { data: slipPhoto } = useQuery({
    queryKey: ['cbs', 'settlement-slip', settlement?.settlement_slip_photo_id],
    queryFn: async () => {
      const { data, error } = await client
        .from('photos')
        .select('storage_path')
        .eq('id', settlement!.settlement_slip_photo_id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!settlement?.settlement_slip_photo_id,
  });

  const disputeMutation = useMutation({
    mutationFn: async (reason: string) => {
      const { error } = await client
        .from('settlements')
        .update({
          settlement_status: 'disputed',
          dispute_reason: reason,
          disputed_at: new Date().toISOString(),
        })
        .eq('id', settlementId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'settlement', settlementId] });
      setShowDisputeForm(false);
      setDisputeReason('');
      toast.success(t("cbs.settlement.disputeSuccess", { defaultValue: "Dispute raised successfully" }));
    },
    onError: () => toast.error(t("cbs.settlement.disputeFailed", { defaultValue: "Failed to raise dispute" })),
  });

  if (isLoading) {
    return (
      <Screen showTopBar showBottomNav activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Screen>
    );
  }

  if (!settlement) {
    return (
      <Screen showTopBar showBottomNav activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="p-6 text-center text-muted-foreground">{t("cbs.settlement.notFound", { defaultValue: "Settlement not found" })}</div>
      </Screen>
    );
  }

  const status = statusConfig[settlement.settlement_status] ?? statusConfig.pending;
  const batchCode = (settlement as any).batches?.batch_code ?? 'N/A';
  const breedName = (settlement as any).batches?.breeds?.name ?? '';
  const integratorName = (settlement as any).organizations?.name ?? 'N/A';

  const deductions = [
    { label: t("cbs.settlement.deductions.docAdvance", { defaultValue: "DOC Advance" }), value: settlement.doc_advance_bdt },
    { label: t("cbs.settlement.deductions.feedAdvance", { defaultValue: "Feed Advance" }), value: settlement.feed_advance_bdt },
    { label: t("cbs.settlement.deductions.medicationAdvance", { defaultValue: "Medication Advance" }), value: settlement.medication_advance_bdt },
    { label: t("cbs.settlement.deductions.otherAdvance", { defaultValue: "Other Advance" }), value: settlement.other_advance_bdt },
    { label: t("cbs.settlement.deductions.mortalityDeduction", { defaultValue: "Mortality Deduction" }), value: settlement.mortality_deduction_bdt },
    { label: t("cbs.settlement.deductions.qualityPenalty", { defaultValue: "Quality Penalty (FCR)" }), value: settlement.quality_penalty_bdt },
    { label: t("cbs.settlement.deductions.otherDeduction", { defaultValue: "Other Deduction" }), value: settlement.other_deduction_bdt },
  ].filter((d) => d.value != null && d.value > 0);

  const bonuses = [
    { label: t("cbs.settlement.bonuses.lowMortality", { defaultValue: "Low Mortality Bonus" }), value: settlement.low_mortality_bonus_bdt },
    { label: t("cbs.settlement.bonuses.earlyHarvest", { defaultValue: "Early Harvest Bonus" }), value: settlement.early_harvest_bonus_bdt },
    { label: t("cbs.settlement.bonuses.biosecurity", { defaultValue: "Biosecurity Bonus" }), value: settlement.biosecurity_bonus_bdt },
  ].filter((b) => b.value != null && b.value > 0);

  // ── SIMPLE MODE VIEW ──────────────────────────────────
  if (isSimpleMode) {
    return (
      <Screen showTopBar={false} showBottomNav activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="bg-card border-b border-border px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 min-h-12 min-w-12 flex items-center justify-center">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <span className="text-2xl">🧾</span>
            <Badge variant={status.variant} className="text-base px-3 py-1">{t(`cbs.settlement.status.${settlement.settlement_status}`, { defaultValue: status.label })}</Badge>
          </div>
        </div>

        <div className="p-4 space-y-4 pb-24">
          {/* Net amount - hero */}
          <Card className="p-6 bg-primary/5 border-primary/20">
            <SimpleMetric emoji="💰" value={fmt(settlement.net_settlement_bdt)} size="lg" color="success" className="justify-center" />
          </Card>

          {/* Key metrics */}
          <SimpleMetricGrid columns={2}>
            <SimpleMetric emoji="🐔" value={settlement.birds_harvested?.toLocaleString() ?? "—"} size="md" />
            <SimpleMetric emoji="⚖️" value={fmtKg(settlement.avg_live_weight_kg)} size="md" />
            {settlement.fcr_achieved != null && (
              <SimpleMetric emoji="📊" value={settlement.fcr_achieved.toFixed(2)} size="md" color={settlement.fcr_achieved <= 1.6 ? 'success' : settlement.fcr_achieved <= 1.8 ? 'warning' : 'destructive'} />
            )}
            <SimpleMetric emoji="💵" value={fmt(settlement.gross_amount_bdt)} size="md" color="info" />
          </SimpleMetricGrid>

          {/* Deductions summary */}
          {deductions.length > 0 && (
            <SimpleMetric emoji="📉" value={fmt(settlement.total_deductions_combined_bdt)} size="md" color="destructive" />
          )}

          {/* Bonuses summary */}
          {bonuses.length > 0 && (
            <SimpleMetric emoji="🏆" value={fmt(settlement.total_bonuses_bdt)} size="md" color="success" />
          )}

          {/* Dispute button */}
          {settlement.settlement_status !== 'disputed' && settlement.settlement_status !== 'paid' && (
            <Button
              variant="outline"
              className="w-full h-16 text-lg border-destructive/30 text-destructive hover:bg-destructive/5 gap-3"
              onClick={() => setShowDisputeForm(true)}
            >
              ⚠️ {t("cbs.settlement.dispute", { defaultValue: "Dispute" })}
            </Button>
          )}

          {showDisputeForm && (
            <Card className="p-4 space-y-3">
              <Textarea
                placeholder={t("cbs.settlement.whyPlaceholder", { defaultValue: "Why?" })}
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={3}
                className="text-lg"
              />
              <div className="flex gap-2">
                <Button variant="destructive" className="flex-1 h-14 text-lg" onClick={() => disputeMutation.mutate(disputeReason)} disabled={!disputeReason.trim() || disputeMutation.isPending}>
                  ⚠️ {disputeMutation.isPending ? '...' : t("common.submit")}
                </Button>
                <Button variant="outline" className="h-14" onClick={() => setShowDisputeForm(false)}>✕</Button>
              </div>
            </Card>
          )}
        </div>
      </Screen>
    );
  }

  // ── NORMAL MODE VIEW ──────────────────────────────────
  return (
    <Screen showTopBar={false} showBottomNav activeTab={activeTab} onTabChange={setActiveTab}>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="p-1">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-foreground">{t("cbs.settlement.title", { defaultValue: "Settlement" })}</h1>
              <Badge variant={status.variant}>{t(`cbs.settlement.status.${settlement.settlement_status}`, { defaultValue: status.label })}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("cbs.batch.title", { defaultValue: "Batch" })} {batchCode} {breedName && `• ${breedName}`}
            </p>
          </div>
          <Receipt className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t("cbs.settlement.integrator", { defaultValue: "Integrator" })}: {integratorName}</span>
          <span>
            {settlement.calculated_at
              ? new Date(settlement.calculated_at).toLocaleDateString()
              : '—'}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Revenue Section */}
        <Card elevation="medium" className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-success" />
            <h2 className="text-sm font-semibold text-foreground">{t("cbs.settlement.revenue", { defaultValue: "Revenue" })}</h2>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cbs.settlement.birdsHarvested", { defaultValue: "Birds Harvested" })}</span>
              <span className="font-medium text-foreground">{settlement.birds_harvested?.toLocaleString() ?? '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cbs.settlement.avgLiveWeight", { defaultValue: "Avg Live Weight" })}</span>
              <span className="font-medium text-foreground">{fmtKg(settlement.avg_live_weight_kg)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cbs.settlement.totalLiveWeight", { defaultValue: "Total Live Weight" })}</span>
              <span className="font-medium text-foreground">{fmtKg(settlement.total_live_weight_kg)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cbs.settlement.growingRate", { defaultValue: "Growing Rate" })}</span>
              <span className="font-medium text-foreground">
                {settlement.performance_rate_per_kg != null
                  ? `${formatCurrency(Number(settlement.performance_rate_per_kg))}/kg`
                  : '—'}
              </span>
            </div>
            {settlement.fcr_achieved != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cbs.settlement.fcrAchieved", { defaultValue: "FCR Achieved" })}</span>
                <span className="font-medium text-foreground">
                  {settlement.fcr_achieved.toFixed(2)}
                  {settlement.fcr_tier && <span className="ml-1 text-xs text-muted-foreground">({settlement.fcr_tier})</span>}
                </span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-semibold">
              <span className="text-foreground">{t("cbs.settlement.grossGrowingFee", { defaultValue: "Gross Growing Fee" })}</span>
              <span className="text-success">{fmt(settlement.gross_amount_bdt)}</span>
            </div>
          </div>
        </Card>

        {/* Deductions */}
        {deductions.length > 0 && (
          <Card elevation="medium" className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-destructive" />
              <h2 className="text-sm font-semibold text-foreground">{t("cbs.settlement.deductionsTitle", { defaultValue: "Deductions" })}</h2>
            </div>
            <div className="space-y-2 text-sm">
              {deductions.map((d) => (
                <div key={d.label} className="flex justify-between">
                  <span className="text-muted-foreground">{d.label}</span>
                  <span className="font-medium text-destructive">-{fmt(d.value)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">{t("cbs.settlement.totalDeductions", { defaultValue: "Total Deductions" })}</span>
                <span className="text-destructive">-{fmt(settlement.total_deductions_combined_bdt)}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Bonuses */}
        {bonuses.length > 0 && (
          <Card elevation="medium" className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-warning" />
              <h2 className="text-sm font-semibold text-foreground">{t("cbs.settlement.bonusesTitle", { defaultValue: "Bonuses" })}</h2>
            </div>
            <div className="space-y-2 text-sm">
              {bonuses.map((b) => (
                <div key={b.label} className="flex justify-between">
                  <span className="text-muted-foreground">{b.label}</span>
                  <span className="font-medium text-success">+{fmt(b.value)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">{t("cbs.settlement.totalBonuses", { defaultValue: "Total Bonuses" })}</span>
                <span className="text-success">+{fmt(settlement.total_bonuses_bdt)}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Net Payment */}
        <Card elevation="high" className="p-4 bg-primary/5 border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <Banknote className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">{t("cbs.settlement.netSettlement", { defaultValue: "Net Settlement" })}</h2>
          </div>
          <div className="text-3xl font-bold text-primary text-center py-2">
            {fmt(settlement.net_settlement_bdt)}
          </div>
          <div className="text-xs text-center text-muted-foreground mt-1">
            {settlement.payment_method && `${t("cbs.settlement.payment", { defaultValue: "Payment" })}: ${settlement.payment_method}`}
            {settlement.payment_reference && ` • ${t("cbs.settlement.ref", { defaultValue: "Ref" })}: ${settlement.payment_reference}`}
          </div>
        </Card>

        {/* Settlement Slip */}
        {slipPhoto && (
          <Card elevation="medium" className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Image className="w-4 h-4 text-info" />
              <h2 className="text-sm font-semibold text-foreground">{t("cbs.settlement.slipVsSystem", { defaultValue: "Settlement Slip vs System" })}</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-2 text-center">{t("cbs.settlement.uploadedSlip", { defaultValue: "Uploaded Slip" })}</p>
                <img src={slipPhoto.storage_path} alt={t("cbs.settlement.slipAlt", { defaultValue: "Settlement slip" })} className="w-full rounded-lg border border-border object-cover aspect-[3/4]" />
              </div>
              <div className="flex flex-col justify-center space-y-2 text-xs">
                <p className="text-muted-foreground text-center mb-1">{t("cbs.settlement.systemCalculation", { defaultValue: "System Calculation" })}</p>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("cbs.settlement.gross", { defaultValue: "Gross" })}</span><span className="font-medium">{fmt(settlement.gross_amount_bdt)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("cbs.settlement.deductionsTitle", { defaultValue: "Deductions" })}</span><span className="font-medium text-destructive">-{fmt(settlement.total_deductions_combined_bdt)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("cbs.settlement.bonusesTitle", { defaultValue: "Bonuses" })}</span><span className="font-medium text-success">+{fmt(settlement.total_bonuses_bdt)}</span></div>
                <Separator />
                <div className="flex justify-between font-bold"><span>{t("cbs.settlement.net", { defaultValue: "Net" })}</span><span className="text-primary">{fmt(settlement.net_settlement_bdt)}</span></div>
              </div>
            </div>
          </Card>
        )}

        {/* Dispute Section */}
        {settlement.settlement_status === 'disputed' && settlement.dispute_reason && (
          <Card elevation="low" className="p-4 bg-destructive/5 border-destructive/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <h2 className="text-sm font-semibold text-destructive">{t("cbs.settlement.disputeRaised", { defaultValue: "Dispute Raised" })}</h2>
            </div>
            <p className="text-sm text-foreground">{settlement.dispute_reason}</p>
            {settlement.dispute_resolution && (
              <div className="mt-2 pt-2 border-t border-destructive/10">
                <p className="text-xs text-muted-foreground">{t("cbs.settlement.resolution", { defaultValue: "Resolution" })}:</p>
                <p className="text-sm text-foreground">{settlement.dispute_resolution}</p>
              </div>
            )}
          </Card>
        )}

        {/* Raise Dispute */}
        {settlement.settlement_status !== 'disputed' && settlement.settlement_status !== 'paid' && (
          <div>
            {!showDisputeForm ? (
              <Button variant="outline" className="w-full border-destructive/30 text-destructive hover:bg-destructive/5" onClick={() => setShowDisputeForm(true)}>
                <AlertTriangle className="w-4 h-4 mr-2" /> {t("cbs.settlement.raiseDispute", { defaultValue: "Raise Dispute" })}
              </Button>
            ) : (
              <Card elevation="medium" className="p-4 space-y-3">
                <Label className="text-sm font-semibold">{t("cbs.settlement.disputeReason", { defaultValue: "Dispute Reason" })}</Label>
                <Textarea placeholder={t("cbs.settlement.disputePlaceholder", { defaultValue: "Explain why you disagree with this settlement..." })} value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} rows={3} />
                <div className="flex gap-2">
                  <Button variant="destructive" className="flex-1" onClick={() => disputeMutation.mutate(disputeReason)} disabled={!disputeReason.trim() || disputeMutation.isPending}>
                    {disputeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t("cbs.settlement.submitDispute", { defaultValue: "Submit Dispute" })}
                  </Button>
                  <Button variant="outline" onClick={() => setShowDisputeForm(false)}>{t("common.cancel")}</Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </Screen>
  );
};
